// ────────────────────────���───────────────────────────────────────
// Contexte d'authentification global — FinanceApp
// Fournit : user, login, logout, loading
// ────────────────────────────────────────────────────────────────
import { createContext, useContext, useState, useEffect } from 'react';
import api, { getToken, setToken, removeToken } from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  // Au démarrage : vérification du token stocké
  useEffect(() => {
    const token = getToken();
    if (token) {
      api.auth.me()
        .then(u => setUser(u))
        .catch(() => removeToken())
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }

    // Logout déclenché par une 401 depuis le service API
    const onLogout = () => { removeToken(); setUser(null); };
    window.addEventListener('financeapp:logout', onLogout);
    return () => window.removeEventListener('financeapp:logout', onLogout);
  }, []);

  /**
   * Connexion : retourne { requiresTwoFa: true } si la 2FA est activée
   * et qu'aucun code n'a été fourni. Sinon stocke le token et met à jour user.
   */
  const login = async (email, password, totpCode, remember_me = false) => {
    const res = await api.auth.login(email, password, totpCode, remember_me);
    if (res.requiresTwoFa) return { requiresTwoFa: true };
    setToken(res.token);
    setUser(res.user);
    return res;
  };

  /**
   * Inscription : crée le compte + la société de démo côté serveur,
   * puis connecte automatiquement (le backend renvoie un token direct,
   * comme /login sans 2FA).
   */
  const register = async (data) => {
    const res = await api.auth.register(data);
    setToken(res.token);
    setUser(res.user);
    return res;
  };

  const logout = () => {
    removeToken();
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const u = await api.auth.me();
      setUser(u);
      return u;
    } catch { return null; }
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth doit être utilisé dans un AuthProvider');
  return ctx;
}
